import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { clientCommand, exerciseCommand, go, placeCommands, rank, workoutCommand, type Command } from "../lib/commands";

/* ⌘K — jump to any client or workout by typing. A native <dialog> so the dim, the focus trap and
 * Escape come from the platform; the list is a listbox the arrow keys walk. Data is fetched once
 * per open through the SAME policies every screen meets (RLS decides what a coach may see). */
const PALETTE_EVENT = "ht:palette-open";

/** Open ⌘K from a control (the top bar's search) — the same palette the shortcut opens. */
export function openPalette(): void {
  window.dispatchEvent(new Event(PALETTE_EVENT));
}

export function CommandPalette({ coachId }: { coachId: string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [named, setNamed] = useState<Command[]>([]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
    }
    // W81 — the top bar's search control opens the SAME palette (`openPalette`), never a second one.
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(PALETTE_EVENT, onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener(PALETTE_EVENT, onOpen); };
  }, []);

  useEffect(() => {
    const d = dialog.current; if (d === null) return;
    if (open && !d.open) { d.showModal(); setQuery(""); setCursor(0); input.current?.focus(); }
    if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const [links, workouts, exercises] = await Promise.all([
        supabase.from("coach_clients").select("client_id, invited_first_name, invited_last_name").eq("coach_id", coachId),
        /* The LIBRARY is `client_id is null` (W17, the one definition every shelf reads) — never `root_workout_id is null`:
           William's item 4 (2026-10-15) makes a client's ad-hoc copy (client set, root null), which the old read listed here. */
        supabase.from("coach_workouts").select("id, name").eq("coach_id", coachId).is("client_id", null).order("name"),
        // The SHELF's own query, narrowed to what a palette row needs — and, like the shelf, it
        // names its owner (`lib/me.ts`: a client-read policy would otherwise add another library).
        supabase.from("coach_exercises").select("id, name").eq("coach_id", coachId).order("name"),
      ]);
      const ids = (links.data ?? []).map((l) => l.client_id).filter((x): x is string => x !== null);
      const profiles = ids.length > 0 ? await supabase.from("profiles").select("id, first_name, last_name").in("id", ids) : { data: [] as { id: string; first_name: string | null; last_name: string | null }[] };
      const byId = new Map((profiles.data ?? []).map((p) => [p.id, p]));
      const clients = (links.data ?? []).flatMap((l) => {
        if (l.client_id === null) return [];
        const p = byId.get(l.client_id);
        const name = [p?.first_name ?? l.invited_first_name, p?.last_name ?? l.invited_last_name].filter(Boolean).join(" ").trim();
        return name === "" ? [] : [clientCommand(l.client_id, name)];
      });
      const ws = (workouts.data ?? []).flatMap((w) => (w.name ? [workoutCommand(w.id, w.name)] : []));
      const xs = (exercises.data ?? []).flatMap((x) => (x.name ? [exerciseCommand(x.id, x.name)] : []));
      if (!cancelled) setNamed([...clients, ...ws, ...xs]);
    })();
    return () => { cancelled = true; };
  }, [open, coachId]);

  const results = useMemo(() => rank(query, [...named, ...placeCommands()]), [query, named]);
  const pick = (c: Command) => { setOpen(false); go(c); };

  return (
    <dialog ref={dialog} className="ht-window ht-palette" aria-label="Jump to" onClose={() => setOpen(false)}
            onCancel={(e) => { e.preventDefault(); setOpen(false); }}>
      <div className="ht-palette-frame">
        <input ref={input} className="ht-palette-input" type="search" placeholder="Jump to a client, a workout, a place…"
               value={query} autoComplete="off" spellCheck={false} role="combobox" aria-expanded="true"
               aria-controls="palette-list" aria-activedescendant={results[cursor] ? `pal-${results[cursor].id}` : undefined}
               onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
               onKeyDown={(e) => {
                 if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
                 else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
                 else if (e.key === "Enter" && results[cursor]) { e.preventDefault(); pick(results[cursor]); }
               }} />
        <ul id="palette-list" role="listbox" className="ht-palette-list">
          {results.length === 0 && <li className="ht-palette-empty ht-caption">Nothing matches. Try a name.</li>}
          {results.map((c, i) => (
            <li key={c.id} id={`pal-${c.id}`} role="option" aria-selected={i === cursor}
                className={`ht-palette-row${i === cursor ? " is-active" : ""}`}
                onMouseEnter={() => setCursor(i)} onClick={() => pick(c)}>
              <span className="ht-palette-title">{c.title}</span>
              <span className="ht-palette-hint">{c.hint}</span>
            </li>
          ))}
        </ul>
        <p className="ht-palette-foot ht-caption"><kbd>↑↓</kbd> move · <kbd>↵</kbd> open · <kbd>esc</kbd> close</p>
      </div>
    </dialog>
  );
}
